/**
 * P41 · labels for the STABLE KEYS the server emits about guardian
 * consent.
 *
 * The server doesn't send phrases: it sends keys (`classroom_chat`, `granted`,
 * `absent`, `grades`). `use_cases::consent` says it explicitly: "they're keys
 * and not phrases because the wording belongs to the client (i18n) and because a phrase on the
 * server would be left untranslated". This file is the only place where those
 * keys turn into text, and it's shared by the two new screens (the
 * guardian's public one and the student's).
 *
 * ── What is NOT here, and why ────────────────────────────────────────────────
 *
 * **Not an age.** The threshold is per-academy configuration
 * (`app_config` → `academy.guardian_consent_age`) and arrives in
 * `MyGuardianship.consentAgeYears`. The phrases that mention it interpolate it;
 * none of them write it.
 *
 * **Not the legal text.** Describing *what's authorized* in each scope with legal
 * weight —what's promised, who it can be claimed against, how it's withdrawn— is
 * the owner's copy and is pending (`PENDIENTE-TEXTO-CONSENTIMIENTO-DUENO`). What's
 * below is the FUNCTIONAL description of what the app does with each scope,
 * which is the interface's business and can't wait.
 *
 * ── Unknown key ───────────────────────────────────────────────────────────
 *
 * `labelFor` returns `undefined` and the screen skips the row. A scope
 * this client doesn't know (a newer server version) isn't shown
 * as "authorized": not showing something is recoverable, asserting a permission that
 * isn't understood is not.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

/** Short name for each scope in `ConsentScope`'s closed catalog. */
export const CONSENT_SCOPE_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  account: msg`Account in the academy`,
  classroom_chat: msg`Classroom chat`,
  forum: msg`Course forum`,
  ai: msg`Artificial intelligence features`,
  photo: msg`Profile photo`,
};

/**
 * Order in which the scopes are offered on the two staff screens that
 * present them as checkboxes (request by email, register in person/paper).
 * The catalog is set by the server (`ConsentScope`); this only decides how it's
 * presented, and lives in one place so the two screens don't diverge.
 */
export const OFFERED_CONSENT_SCOPES = [
  "account",
  "classroom_chat",
  "forum",
  "ai",
  "photo",
] as const;

/** What's almost always requested or recorded: the account and the classroom. */
export const DEFAULT_CONSENT_SCOPES: readonly string[] = ["account", "classroom_chat"];

/**
 * What the app does with each scope. Functional description, not legal.
 *
 * `photo` says what actually happens today: the avatars' rule zero
 * (`avatar::policy`) **does not consult consent** and still doesn't publish a
 * minor student's photo even if the scope is on record. Promising the opposite
 * on the screen where a family grants consent would be the most costly lie of the
 * five.
 */
export const CONSENT_SCOPE_DESCRIPTIONS: Readonly<Record<string, MessageDescriptor>> = {
  account: msg`Having an account and taking part in the academy's activities.`,
  classroom_chat: msg`Writing in the chat of their group, with their classmates and their teacher.`,
  forum: msg`Opening threads and replying in the forum of their courses.`,
  ai: msg`Using the artificial intelligence features, when the academy has them switched on.`,
  photo: msg`Their profile picture being shown inside the academy. Today the academy does not show a minor's picture even with this authorization; it is recorded for the day that policy changes.`,
};

/** A link's consent status (`guardian_link.consent_status`). */
export const CONSENT_STATUS_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  pending: msg`Awaiting authorization`,
  granted: msg`Authorized`,
  revoked: msg`Withdrawn`,
};

/**
 * What a linked guardian gets to see (`MyGuardianship.sees`).
 * The keys come from the real streams, not from a made-up list.
 */
export const GUARDIAN_SEES_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  enrollments: msg`The courses you are enrolled in`,
  grades: msg`Your grades`,
  attendance: msg`Your attendance`,
  notifications: msg`The notifications the academy sends about you`,
};

/**
 * How the age was resolved (`MyGuardianship.ageResolution`).
 *
 * `absent` is the MAJORITY case, not the rare one: of the seven entry paths into
 * the system only one records the age, and the importer through which most
 * of the student body enters doesn't even have a column for it. That's why its text explains the consequence
 * (counts as a minor) instead of just saying a data point is missing.
 */
export const AGE_RESOLUTION_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  known: msg`Your academy has your date of birth on file.`,
  absent: msg`Your academy does not have your date of birth on file. While it is missing, you count as a minor.`,
  unreadable: msg`The date of birth on file cannot be read. While that is the case, you count as a minor.`,
};

/** A key's label, or `undefined` if this client doesn't know it. */
export function labelFor(
  table: Readonly<Record<string, MessageDescriptor>>,
  key: string,
): MessageDescriptor | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}
